import styled, { css } from "styled-components";
import { useCurrency, useTournamentDetails } from 'hooks';
import { useSoundStore } from 'utils/sound-store';
import { Sounds } from 'types/sound';
import { Modal } from 'components/Modal';
import { Balance, BalanceValue, Text, Button } from 'components/InsufficientBalanceModal';
import { AnimButton, Center } from "utils/constants";
import { useState } from "react";
import { BuyIn } from "types";
import { useStore } from "utils/store";
import { useTranslation } from "react-i18next";

interface RegisterConfirmModalProps {
  show: boolean;
  balance: number;
  amount: number;
  rejoin: boolean;
  onConfirm: (buyIn: BuyIn) => void;
  onClose: () => void;
}

export const RegisterConfirmModal: React.FC<RegisterConfirmModalProps> = ({
  show,
  balance, 
  amount,
  rejoin: _rejoin,
  onConfirm,
  onClose,
}) => {
  const { buyIn, buyIns } = useTournamentDetails();
  const rejoin = !!buyIns?.length && _rejoin;

  const { parseCurrency } = useCurrency();
  const play = useSoundStore(state => state.play);
  const { t } = useTranslation();

  const [selectedBuyIn, setSelectedBuyIn] = useState(buyIn);
  const showInsufficient = useStore(state => state.showInsufficient);

  return (
    <Container show={show && !showInsufficient} onClose={onClose} $rejoin={rejoin}>
      <StyledBalance>
        {t('BALANCE')}: <BalanceValue>{parseCurrency(balance || 0)}</BalanceValue>
      </StyledBalance>
      {
        rejoin ? (
          <>
            <Message>
              {t('Your account will be charged:')} <ToCharge>${selectedBuyIn?.buyIn}</ToCharge>
            </Message>
            <BuyIns>
              {[...buyIns].sort((a, b) => a.buyIn - b.buyIn).map((value, index) => (
              <BuyInButton
                key={index}
                $active={selectedBuyIn?.buyIn === value.buyIn}
                onPointerDown={() => play(Sounds.MouseClick)}
                onPointerUp={() => {
                  setSelectedBuyIn(value);
                }}
                data-testid={`rejoin-buy-in-${value.buyIn}-button`}
              >
                <Price>
                  {parseCurrency(value.buyIn)}
                </Price>
              </BuyInButton>
              ))}
            </BuyIns>
          </>
        ) : (
          <>
            <Message>
              {t('Your account will be charged:')}
            </Message>
            <Amount>
              $<span>{amount}</span>
            </Amount>
          </>
        )
      }
      <Button
        onPointerDown={() => play(Sounds.MouseClick)}
        onPointerUp={() => {
          onConfirm(selectedBuyIn);
        }}
      >
        <span>{t('CONFIRM')}</span>
      </Button>
      <Cancel onPointerUp={onClose}>{t('Cancel')}</Cancel>
    </Container>
  );
}

const StyledBalance = styled(Balance)`
  margin-top: 35px;

  @media (orientation: portrait) {
    margin-top: 7.7vmin;
  }
`;

const BuyIns = styled(Center)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 12.2px;
  width: 100%;
  margin: 18px auto;
  max-width: 200px;

  @media (orientation: portrait) {
    row-gap: 1.5vh;
    column-gap: 1vw;
    max-width: 60vw;
  }
`;

const Price = styled.div`
  width: 200%;
  margin-top: 6%;
  text-shadow: 0 1px 0 #4e4e57;
  font-family: Teko;
  font-size: 21.4px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: center;
  color: #cfcfcf;
  position: relative;
  user-select: none;

  &::first-letter {
    font-weight: 600;
    font-size: 80%;
  }

  @media (orientation: portrait) {
    font-size: clamp(1.75rem, -1.66rem + 7.179vh, 3.5rem);
  } 
`;

const Transition = styled(AnimButton)`
  overflow: hidden;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: #fff;
    opacity: 0;
  }

  &:active::after {
    opacity: 0.9;
  }
`;

const hover = () => {
  return css`
    text-shadow: none;
    filter: drop-shadow(0 1px 0 #fff);
    background-image: linear-gradient(
      to bottom,
      #000 28%,
      rgba(87, 84, 84, 0.82) 41%,
      #000 56%
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  `;
};

const Active = styled(Transition)<{ $active: boolean }>`
  font-family: Teko;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover ${Price}, &:hover${Text} { ${hover()} }

  & ${Price}, ${Text} {
    ${(props) => props.$active && hover()}
  }
`;

const buttonHover = () => {
  return css`
    border: solid 0.3px #fff;
    box-shadow: 0 0 5px 0 #fff, inset 0 1px 0 0 #fff;
    background-image: linear-gradient(to bottom, #898a9f 0%, #fbfbfc 52%, #828399 100%);

    @media (orientation: portrait) {
      box-shadow: 0 0 15px 0 #fff, inset 0 0.3vh 0 0 #fff;
    } 
  `;
};

const BuyInButton = styled(Active)`
  width: 50.8px;
  height: 32.9px;
  margin: 0 2.85px;
  outline: none;
  border-radius: 4.2px;
  box-sizing: border-box;
  border: solid 1px #737375;
  box-shadow: inset 0 2px 0 0 #7e7e7e;
  background-image: linear-gradient(to bottom, #484852 0%, #7b7b7c 52%, #42434e 100%);
  pointer-events: ${props => props.$active ? "none" : "auto"};

  &:hover { ${buttonHover()} }

  ${(props) => props.$active && buttonHover()}

  @media (orientation: portrait) {
    width: 11.7vh;
    height: clamp(2.25rem, -1rem + 7.179vh, 3.5rem);
    border-radius: 0.5vh;
  }
`;

const ToCharge = styled.span`
  color: #0f0;
`;

const Container = styled(Modal)<{ $rejoin?: boolean }>`
  width: 250px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (orientation: portrait) {
    width: 84vw;
  }
`;

const Message = styled(Text)`
  margin: 22px auto 0;

  @media (orientation: portrait) {
    margin-top: 8vmin;
  }
`;

const Amount = styled.div`
  font-size: 30.9px;
  color: #ffd797;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  font-weight: 600;
  line-height: 29.4px;
  margin: 19px auto 14px;

  span {
    font-size: 38.7px;
    font-weight: 700;
    line-height: 30px;
  }

  @media (orientation: portrait) {
    font-size: clamp(2.375rem, 0.125rem + 10vmin, 4.125rem);
    margin: 8.8vmin auto 9.9vmin;

    span {
      font-size: clamp(3rem, 0.107rem + 12.857vmin, 5.25rem);
    }
  }
`;

const Cancel = styled.button`
  border: none;
  background: transparent;
  font-size: 16px;
  text-decoration: underline;
  color: #ffffff;
  font-family: Teko;
  margin: 19px auto 14px auto;
  height: 25px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (orientation: portrait) {
    font-size: clamp(1.25rem, 0.125rem + 5vmin, 2.125rem);
    margin-top: 7.7vmin;
    margin-bottom: 5vmin;
  }
`;
